<template>
  <div class="page-root">
    <section id="hero" class="hero-section">
      <!-- Placeholder for video -->

      <video autoplay loop muted playsinline class="hero-video">
        <source src="/mini-smooth-long.mov" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <!-- Placeholder for logo -->
      <div id="hero-logo">
        <img src="@/assets/assm-logo.png" class="hero-logo" />
      </div>

      <!-- Top Pinned Mobile Message -->
      <p class="hero-intro">
        What's <em class="italic">really</em> on your mind?
      </p>

      <div class="hero-container">


        <div class="hero-subhead">
          <h1 class="hero-title">Peeple</h1>
          <p class="hero-subtext">
            Share your Google searches in real-time
          </p>
        </div>

        <!-- JustineCutie component inline -->
        <div class="signup-box">
          <form @submit.prevent="submitJustineForm" class="signup-form">
            <div class="input-container">
              <input v-model="justineEmail" type="email" placeholder="your@email.com" class="signup-input" />
              <button type="submit" :disabled="isJustineSubmitting" class="signup-button">
                {{ isJustineSubmitting ? "..." : "Join Waitlist" }}
              </button>
            </div>
            <p v-if="justineShowToast" class="success-message">
              🎉 You're on the waitlist!
            </p>
            <p v-if="justineEmailError" class="error-message">{{ justineEmailError }}</p>
            <p class="privacy-note">We'll never share your email with anyone else.</p>
          </form>
        </div>
      </div>
    </section>

    <section class="promo-block">
      <h2>
        The <span class="bold">easiest way</span> to see<br />
        what your friends are <em class="italic">really</em><br />
        <span class="bold">thinking about</span>.
      </h2>
    </section>

    <!-- WhyPeeple component inline -->
    <section id="why-peeple" class="section-gray">
      <div class="features-section">
        <div class="features-intro">
          <h2 class="features-heading">
            Peeple is social media that publishes your Google searches.
          </h2>
          <p class="features-subtext">
            We're building a social network that shows what people are really thinking.
          </p>
        </div>

        <div class="features-grid">
          <!-- Feature Box 1 -->
          <div class="feature-box">
            <div class="feature-icon">
              <svg class="icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3 class="feature-title">The thrill of vulnerability</h3>
            <p class="feature-description">
              Performance is so over. Show your friends who you really are, and connect based
              on what you're really curious about.
            </p>
          </div>

          <!-- Feature Box 2 -->
          <div class="feature-box">
            <div class="feature-icon">
              <svg class="icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
            <h3 class="feature-title">Social media -- without the management</h3>
            <p class="feature-description">
              All other social media requires work, thought, and management on your part. Peeple is the world's first
              passive social media. Set it, forget it, and check back in whenever you want to see what your friends are
              up
              to.
            </p>
          </div>

          <!-- Feature Box 3 -->
          <div class="feature-box">
            <div class="feature-icon">
              <svg class="icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z">
                </path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <h3 class="feature-title">It's a brave new world</h3>
            <p class="feature-description">
              So be brave! Challenge yourself to be transparent in a world that encourages carefully crafted online
              personas.
            </p>
          </div>

          <!-- Feature Box 4 -->
          <div class="feature-box">
            <div class="feature-icon">
              <svg class="icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <h3 class="feature-title">Discover the real you</h3>
            <p class="feature-description">
              Get to know the unfiltered you. See patterns in your searches, and connect with others on similar journeys
              of discovery.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- EarlyAccess component inline -->
    <section id="early-access" class="section-white">
      <div class="signup-section">
        <div class="signup-intro">
          <h2 class="signup-heading">Ready to be brave?</h2>
          <p class="signup-subtext">
            Sign up now for early access and help shape the future of authentic social networking.
          </p>
        </div>

        <!-- Signup form -->
        <div class="signup-form-box">
          <form @submit.prevent="submitEarlyAccessForm" class="signup-form">
            <div>
              <input v-model="earlyAccessEmail" type="email" placeholder="you@email.com" required class="email-input" />
              <p v-if="earlyAccessEmailError" class="error-message">{{ earlyAccessEmailError }}</p>
            </div>
            <button type="submit" :disabled="isEarlyAccessSubmitting" class="submit-button">
              {{ isEarlyAccessSubmitting ? "Submitting..." : "Join the Waitlist" }}
            </button>
          </form>

          <p v-if="earlyAccessShowToast" class="success-message">
            🎉 You're on the waitlist!
          </p>

          <p class="privacy-note-dark">We'll never share your email with anyone else.</p>
        </div>

        <!-- Timeline Section -->
        <div class="timeline-box">
          <h3 class="timeline-heading">What happens next?</h3>

          <!-- Desktop timeline -->
          <div class="timeline-desktop">
            <div class="timeline-line"></div>
            <div class="timeline-steps">
              <div class="timeline-step">
                <div class="step-number">1</div>
                <h4>Sign Up</h4>
                <p>Join our waitlist</p>
              </div>
              <div class="timeline-step">
                <div class="step-number">2</div>
                <h4>Receive Invite</h4>
                <p>Get your exclusive access code</p>
              </div>
              <div class="timeline-step">
                <div class="step-number">3</div>
                <h4>Set Up Profile</h4>
                <p>Connect your search history</p>
              </div>
              <div class="timeline-step">
                <div class="step-number">4</div>
                <h4>Start Sharing</h4>
                <p>Begin your authentic journey</p>
              </div>
            </div>
          </div>

          <!-- Mobile timeline -->
          <div class="timeline-mobile">
            <div class="timeline-step">
              <div class="step-number small">1</div>
              <h4>Sign Up</h4>
              <p>Join our waitlist with your email</p>
            </div>
            <div class="timeline-step">
              <div class="step-number small">2</div>
              <h4>Receive Invite</h4>
              <p>Get your exclusive access code via email</p>
            </div>
            <div class="timeline-step">
              <div class="step-number small">3</div>
              <h4>Set Up Profile</h4>
              <p>Connect your search history and customize your profile</p>
            </div>
            <div class="timeline-step">
              <div class="step-number small">4</div>
              <h4>Start Sharing</h4>
              <p>Begin your authentic journey on Peeple</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="page-footer">
      <p>&copy; {{ new Date().getFullYear() }} Peeple. All rights reserved.</p>
      <nav>
      </nav>
    </footer>
  </div>
</template>

<script setup>
import { ref } from 'vue'

// JustineCutie form logic
const justineEmail = ref('')
const justineEmailError = ref('')
const isJustineSubmitting = ref(false)
const justineShowToast = ref(false)

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbziY2gfd4BhCLUqqABTuKZywZK-P879WiFJeDAHCv4MPq6IhBcuOB9X6Z5USN0Q7eCXoQ/exec";

const submitJustineForm = async () => {
  justineEmailError.value = '';
  if (!justineEmail.value) {
    justineEmailError.value = 'Email is required';
    return;
  }
  if (!validateEmail(justineEmail.value)) {
    justineEmailError.value = 'Please enter a valid email address';
    return;
  }

  isJustineSubmitting.value = true;

  try {
    const formData = new URLSearchParams();
    formData.append("email", justineEmail.value);

    const res = await fetch(APPS_SCRIPT_URL, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Failed to submit");

    justineShowToast.value = true;
    justineEmail.value = '';
    setTimeout(() => (justineShowToast.value = false), 5000);
  } catch (err) {
    justineEmailError.value = 'Something went wrong. Please try again.';
  } finally {
    isJustineSubmitting.value = false;
  }
};

const earlyAccessEmail = ref('');
const earlyAccessEmailError = ref('');
const isEarlyAccessSubmitting = ref(false);
const earlyAccessShowToast = ref(false);

const submitEarlyAccessForm = async () => {
  earlyAccessEmailError.value = '';
  if (!earlyAccessEmail.value) {
    earlyAccessEmailError.value = 'Email is required';
    return;
  }
  if (!validateEmail(earlyAccessEmail.value)) {
    earlyAccessEmailError.value = 'Please enter a valid email address';
    return;
  }

  isEarlyAccessSubmitting.value = true;

  try {
    const formData = new URLSearchParams();
    formData.append("email", earlyAccessEmail.value);

    const res = await fetch(APPS_SCRIPT_URL, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Failed to submit");

    earlyAccessShowToast.value = true;
    earlyAccessEmail.value = '';
    setTimeout(() => (earlyAccessShowToast.value = false), 5000);
  } catch (err) {
    earlyAccessEmailError.value = 'Something went wrong. Please try again.';
  } finally {
    isEarlyAccessSubmitting.value = false;
  }
};
</script>

<style>
@font-face {
  font-family: 'Product Sans';
  src: url('@/assets/Product Sans Regular.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Playfair Display';
  src: url('@/assets/PlayfairDisplay-Regular.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Product Sans';
  line-height: 1.5;
  color: #000;
}

.page-root {
  width: 100%;
  overflow-x: hidden;
}

/* Hero section */
.hero-section {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 0 24px;
  text-align: center;
}

.hero-video {
  opacity: 0.7;
  z-index: 0;
  position: absolute;
  inset: 0;
  width: 100vw;
  height: 100%;
  justify-items: center;
  align-self: center;
  object-fit: cover;
}

.hero-logo {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 5rem;
  height: 5rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  z-index: 10;
}

.hero-intro {
  position: absolute;
  top: 1.5rem;
  left: 1rem;
  right: 1rem;
  font-size: 1.5rem;
  font-family: 'Playfair Display';
  color: black;
  z-index: 10;
}

.hero-container {
  position: relative;
  z-index: 10;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 6rem;
  padding-bottom: 2rem;
  box-sizing: border-box;
}

.hero-title {
  font-size: 6rem;
  color: black;
  margin-top: 3rem;
  line-height: 1;
}

.hero-subhead {
  margin-top: 3rem;
  margin-bottom: 4rem;
}

.hero-subtext {
  font-size: 1.875rem;
  font-family: 'Playfair Display';
  color: black;
  line-height: 1.4;
}

.success-message {
  color: white;
  background-color: black;
  padding: 1rem;
  margin-top: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
}

/* Promo block */
.promo-block {
  padding: 4rem 1.5rem;
  text-align: center;
  background-color: #21B5FF;
  color: white;
}

.promo-block h2 {
  font-size: 2rem;
  line-height: 1.3;
  font-weight: normal;
}

/* Section styles */
.section-gray {
  background-color: #f3f4f6;
}

.section-white {
  background-color: white;
}

/* Footer */
.page-footer {
  background-color: #111827;
  color: white;
  padding: 2rem 1.5rem;
  text-align: center;
}

.page-footer nav {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 1.5rem;
}

.page-footer a {
  color: #9ca3af;
  text-decoration: none;
}

.page-footer a:hover {
  color: white;
  text-decoration: underline;
}

/* Utility classes */
.bold {
  font-weight: 700;
}

.italic {
  font-style: italic;
  font-weight: 300;
}

/* JustineCutie component styles */
.signup-box {
  background-color: #21B5FF;
  padding: 2rem;
  max-width: 640px;
  width: 100%;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  opacity: 0.9;
  margin-top: 2rem;
}

.signup-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.input-container {
  position: relative;
  width: 100%;
}

.signup-input {
  height: 48px;
  width: 100%;
  padding: 0 1rem;
  padding-right: 9rem;
  font-size: 1rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
}

.signup-button {
  position: absolute;
  top: 4px;
  right: 4px;
  height: 40px;
  padding: 0 1rem;
  background-color: black;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
}

.signup-button:disabled {
  opacity: 0.5;
  cursor: default;
}

.error-message {
  color: red;
  font-size: 0.875rem;
}

.privacy-note {
  font-size: 0.75rem;
  color: white;
  opacity: 0.8;
  margin-top: 0.5rem;
}

.privacy-note-dark {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}

/* WhyPeeple component styles */
.features-section {
  width: 100%;
  padding: 4rem 1.5rem;
  box-sizing: border-box;
}

.features-intro {
  max-width: 900px;
  margin: 0 auto 3rem auto;
  text-align: center;
}

.features-heading {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #000;
}

.features-subtext {
  font-size: 1.125rem;
  color: #555;
  line-height: 1.6;
}

.features-grid {
  display: grid;
  gap: 2rem;
}

.feature-box {
  background-color: white;
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid #e0e0e0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.feature-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(59, 130, 246, 0.1);
  padding: 1rem;
  border-radius: 999px;
  width: fit-content;
  margin: 0 auto;
}

.icon {
  width: 2rem;
  height: 2rem;
  color: #3b82f6;
}

.feature-title {
  font-size: 1.5rem;
  font-weight: 600;
}

.feature-description {
  font-size: 1rem;
  color: #555;
  line-height: 1.6;
}

/* EarlyAccess component styles */
.signup-section {
  padding: 4rem 1.5rem;
  text-align: center;
  max-width: 960px;
  margin: 0 auto;
}

.signup-heading {
  font-size: 2rem;
  font-weight: 700;
}

.signup-subtext {
  max-width: 800px;
  margin: 1rem auto;
  font-size: 1rem;
  color: #6b7280;
}

.signup-form-box {
  max-width: 480px;
  margin: 2rem auto;
}

.email-input {
  width: 100%;
  height: 40px;
  padding: 0 1rem;
  font-size: 1rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
}

.submit-button {
  width: 100%;
  height: 40px;
  background-color: #21B5FF;
  color: black;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 1rem;
}

.submit-button:hover {
  background-color: #1ca2e6;
}

.timeline-box {
  margin-top: 4rem;
  max-width: 768px;
  margin-left: auto;
  margin-right: auto;
}

.timeline-heading {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
}

.timeline-desktop {
  display: none;
}

.timeline-step h4 {
  font-size: 1rem;
  margin-top: 0.5rem;
  font-weight: 600;
}

.timeline-step p {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #21B5FF;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin: 0 auto;
}

.step-number.small {
  width: 32px;
  height: 32px;
}

.timeline-mobile {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

/* Media queries */
@media (min-width: 640px) {
  .hero-intro {
    position: static;
    margin-bottom: 2.5rem;
    font-size: 2.25rem;
  }

  .hero-logo {
    width: 6rem;
    height: 6rem;
  }

  .hero-title {
    font-size: 14rem;
    margin-top: 0;
  }

  .hero-subhead {
    margin-top: -3rem;
    margin-bottom: 6rem;
  }

  .hero-subtext {
    font-size: 3rem;
  }

  .promo-block h2 {
    font-size: 3rem;
  }

  .signup-heading {
    font-size: 3rem;
  }
}

@media (min-width: 768px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }

  .signup-subtext {
    font-size: 1.25rem;
    line-height: 1.6;
  }

  .timeline-desktop {
    display: block;
    position: relative;
  }

  .timeline-line {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #e5e7eb;
    transform: translateY(-50%);
    z-index: 0;
  }

  .timeline-steps {
    display: flex;
    justify-content: space-between;
    position: relative;
    z-index: 1;
  }

  .timeline-step {
    text-align: center;
    max-width: 120px;
  }

  .timeline-mobile {
    display: none;
  }
}
</style>